import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Package, RefreshCw } from 'lucide-react';
import { fetchDeliveryChallans } from '@/app/services/deliveryChallanService';
import type { DeliveryChallanStatus } from '@/app/types/deliveryChallan';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { cn } from '@/lib/utils';

const statusFilters: Array<'all' | DeliveryChallanStatus> = [
    'all',
    'packed',
    'shipped',
    'delivered',
    'cancelled',
];

const statusBadgeVariant: Record<DeliveryChallanStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
    draft: 'secondary',
    confirmed: 'secondary',
    packed: 'default',
    shipped: 'warning',
    delivered: 'success',
    cancelled: 'destructive',
    completed: 'success',
};

const humanizeStatus = (status: string) =>
    status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function DeliveryChallansPage() {
    const [selectedStatus, setSelectedStatus] = useState<'all' | DeliveryChallanStatus>('all');

    const { data, isLoading, refetch, isFetching } = useQuery({
        queryKey: ['delivery-challans', selectedStatus],
        queryFn: () =>
            fetchDeliveryChallans(
                selectedStatus === 'all' ? {} : { status: selectedStatus },
            ),
    });

    const challans = data?.items || [];

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader
                title="Deliveries"
                rightAction={
                    <button
                        onClick={() => refetch()}
                        className="flex items-center justify-center w-10 h-10 rounded-full active:bg-accent"
                        disabled={isFetching}
                    >
                        <RefreshCw
                            className={cn(
                                "h-5 w-5 text-muted-foreground",
                                isFetching && "animate-spin"
                            )}
                        />
                    </button>
                }
            />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                {/* Status filter chips */}
                <div className="p-4 pb-2">
                    <div className="flex flex-wrap gap-2">
                        {statusFilters.map((status) => (
                            <MobileButton
                                key={status}
                                variant={status === selectedStatus ? 'primary' : 'secondary'}
                                size="sm"
                                onClick={() => setSelectedStatus(status)}
                            >
                                {status === 'all' ? 'All' : humanizeStatus(status)}
                            </MobileButton>
                        ))}
                    </div>
                </div>

                {isLoading ? (
                    <ChallanSkeleton />
                ) : challans.length === 0 ? (
                    <MobileEmptyState
                        icon={<Package className="h-16 w-16" />}
                        title="No Delivery Challans"
                        description={
                            selectedStatus === 'all'
                                ? 'No delivery challans assigned yet.'
                                : `No delivery challans with status "${humanizeStatus(selectedStatus)}".`
                        }
                    />
                ) : (
                    <div className="p-4 pt-2 space-y-3">
                        {challans.map((challan) => {
                            const transactionDate = challan.transaction_date
                                ? format(parseISO(challan.transaction_date), 'dd MMM yyyy')
                                : '—';

                            return (
                                <Link
                                    key={challan.id}
                                    to={`/deliveries/${challan.id}`}
                                    className="block"
                                >
                                    <div className="mobile-card active:bg-accent transition-colors">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-base font-bold text-foreground truncate">
                                                {challan.document_number}
                                            </p>
                                            <MobileBadge variant={statusBadgeVariant[challan.status] ?? 'secondary'}>
                                                {humanizeStatus(challan.status)}
                                            </MobileBadge>
                                        </div>

                                        <p className="text-sm text-muted-foreground mt-1">
                                            {transactionDate}
                                        </p>

                                        {challan.party?.name && (
                                            <p className="text-sm text-muted-foreground mt-0.5">
                                                {challan.party.name}
                                            </p>
                                        )}

                                        {challan.vehicle_number && (
                                            <p className="text-sm text-muted-foreground mt-0.5">
                                                Vehicle: {challan.vehicle_number}
                                            </p>
                                        )}

                                        {/* What this drop physically is, so the load can be
                                            checked off without opening each challan. */}
                                        {challan.packing_summary && (
                                            <div className="flex items-center gap-1.5 mt-2 text-sm font-medium text-foreground">
                                                <Package className="h-4 w-4 text-primary flex-shrink-0" />
                                                <span className="truncate">{challan.packing_summary}</span>
                                            </div>
                                        )}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function ChallanSkeleton() {
    return (
        <div className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="mobile-card space-y-3">
                    <div className="flex items-center justify-between gap-3">
                        <MobileSkeleton className="h-5 w-32" />
                        <MobileSkeleton className="h-5 w-16 rounded-full" />
                    </div>
                    <MobileSkeleton className="h-4 w-24" />
                    <MobileSkeleton className="h-4 w-40" />
                </div>
            ))}
        </div>
    );
}
